import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FounderNav } from '../components/FounderNav';
import {
  fetchFounderAudit,
  fetchFounderCommunities,
  type FounderAuditEntry,
  type FounderCommunity,
} from '../lib/api';

/**
 * Live audit feed for the founder. Renders every audit_log row newest
 * first across every community, with filters by action substring and
 * community. Auto-refreshes every 15 seconds so a moderation suspend
 * or a tripwire grant shows up without manual reload.
 *
 * The audit feed is the founder's primary "what's happening right now"
 * view — every moderator action, every user-status flip, every
 * grant/revoke. Pair this with the founders@ tripwire email and a
 * hijacked session becomes visible almost immediately: the email
 * arrives, you open the feed, you see the action it referenced.
 */
const REFRESH_MS = 15_000;

function fmtRelative(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  const mo = Math.floor(d / 30);
  return `${mo} month${mo === 1 ? '' : 's'} ago`;
}

function actionTone(action: string): 'green' | 'clay' | 'neutral' {
  if (
    action.startsWith('user.banned') ||
    action.startsWith('user.suspended') ||
    action.startsWith('moderator.granted') ||
    action.startsWith('csam.')
  )
    return 'clay';
  if (
    action.startsWith('user.reinstated') ||
    action.startsWith('user.unbanned') ||
    action.startsWith('user.unsuspended') ||
    action.startsWith('event.dismissed')
  )
    return 'green';
  return 'neutral';
}

export function FounderAudit() {
  const [entries, setEntries] = useState<FounderAuditEntry[] | null>(null);
  const [communities, setCommunities] = useState<FounderCommunity[]>([]);
  const [actionFilter, setActionFilter] = useState('');
  const [communityFilter, setCommunityFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState<number>(Date.now());

  const refresh = useCallback(async () => {
    const res = await fetchFounderAudit({
      action: actionFilter || undefined,
      communityId: communityFilter || undefined,
      limit: 100,
    });
    if (res.ok) {
      setEntries(res.data.entries);
      setLastRefreshAt(Date.now());
    } else if (res.stale) {
      setStale(true);
    } else {
      setError(res.error);
    }
  }, [actionFilter, communityFilter]);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), REFRESH_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    fetchFounderCommunities().then((res) => {
      if (res.ok) setCommunities(res.data.communities);
    });
  }, []);

  if (stale) {
    return (
      <main className="wide">
        <div className="card">
          <h1>Session is too old</h1>
          <p className="muted">Founder reads require a fresh sign-in. Reload from the home page.</p>
          <p>
            <Link to="/">← Home</Link>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="wide">
      <div className="card">
        <FounderNav />
        <h1>Audit feed</h1>
        <p className="muted">
          Every moderator and founder write across every community. Refreshes every{' '}
          {REFRESH_MS / 1000}s · last refresh {fmtRelative(new Date(lastRefreshAt).toISOString())}.
        </p>

        <div className="founder-audit-filters">
          <label className="field-label">
            Action contains
            <input
              type="search"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="user., moderator., conversation., crisis…"
            />
          </label>
          <label className="field-label">
            Community
            <select value={communityFilter} onChange={(e) => setCommunityFilter(e.target.value)}>
              <option value="">All communities</option>
              {communities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && <p className="error">{error}</p>}
        {!entries && <p className="muted">Loading…</p>}
        {entries && entries.length === 0 && (
          <p className="muted">No audit entries match those filters.</p>
        )}

        {entries && entries.length > 0 && (
          <ul className="audit-feed">
            {entries.map((e) => (
              <li key={e.id} className="audit-feed-row">
                <div className="audit-feed-head">
                  <span className={`audit-action tone-${actionTone(e.action)}`}>{e.action}</span>
                  <span className="muted">{e.communitySlug ? `· ${e.communitySlug}` : ''}</span>
                  <span className="muted audit-feed-when">
                    {fmtRelative(e.createdAt)} · {new Date(e.createdAt).toLocaleString()}
                  </span>
                </div>
                <div className="audit-feed-meta muted">
                  {e.actorEmail ?? '(system)'} → {e.targetType}{' '}
                  <code>{e.targetId.slice(0, 8)}</code>
                  {e.eventId && (
                    <>
                      {' · '}
                      <Link to={`/moderation/events/${e.eventId}`}>open event →</Link>
                    </>
                  )}
                </div>
                {e.metadata !== null && e.metadata !== undefined && (
                  <pre className="audit-meta">{JSON.stringify(e.metadata, null, 2)}</pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
